// Simple API utility for handling environment-aware API calls
// Uses runtime environment variables loaded from runtime-config.js

// Extend window interface to include runtime config
declare global {
  interface Window {
    ENV?: {
      API_BASE_URL?: string;
      NODE_ENV?: string;
    };
  }
}

/**
 * Get runtime configuration from window.ENV (loaded at startup)
 */
const getRuntimeConfig = () => {
  return {
    apiBaseUrl: window.ENV?.API_BASE_URL || '',
    nodeEnv: window.ENV?.NODE_ENV || 'development',
  };
};

/**
 * Build API URL with environment-aware base URL
 * @param endpoint - API endpoint starting with /api/
 * @returns Full URL for the API call
 */
export const buildApiUrl = (endpoint: string): string => {
  // Ensure endpoint starts with /api/
  if (!endpoint.startsWith('/api/')) {
    endpoint = `/api/${endpoint.replace(/^\//, '')}`;
  }
  
  const config = getRuntimeConfig();
  
  // Development: empty base URL → relative URL (Vite's server.proxy forwards /api -> backend)
  // Production: base URL set → absolute URL to separate domain
  return config.apiBaseUrl ? `${config.apiBaseUrl}${endpoint}` : endpoint;
};

/**
 * Enhanced fetch that automatically uses the correct API base URL
 * @param endpoint - API endpoint (e.g., '/api/projects' or 'projects')
 * @param options - Standard fetch options
 * @returns Promise<Response>
 */
export const apiFetch = async (endpoint: string, options?: RequestInit): Promise<Response> => {
  const url = buildApiUrl(endpoint);
  return fetch(url, options);
};

// Environment info for debugging
export const getApiConfig = () => {
  const config = getRuntimeConfig();
  return {
    baseUrl: config.apiBaseUrl,
    environment: config.nodeEnv,
    isDevelopment: !config.apiBaseUrl,
    isProduction: !!config.apiBaseUrl,
  };
}; 